import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { global } from "@shared/libs/Global";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import SearchPanel from "@widgets/SearchPanel";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { fnGetDataSet, callApi, fnGetTableById } from "@shared/api/restApi";
import { showDialog } from "@widgets/CommonDialog";
import ProjectFolder from "./ProjectFolder";
import { showPopupModal } from "@widgets/CommonModal";
import MultiPopup from "@widgets/commonpopup/MultiPopup";
import { PopupSettings } from "@widgets/commonpopup/PopupSettings";

/**
 * SN_SFA_PJT_REG_002_POP_002 — WBS 진행등록 팝업 (ISS2026100053)
 * 메타 구동: 조회조건 sys_popupctrl(cd_form='search'), 그리드 sys_popupgridcolumn(grid1).
 *   - 프로젝트 선택 → WBS 행별 계획·실적 일자/진척(rt_work) 편집.
 *   - 행추가(대표ACT / 선택한 대표ACT 아래 세부ACT)·행삭제(대표ACT 는 세부ACT 까지, 실적 있는 행은 불가).
 *     신규 행만 ACTIVITY 를 도움창(POPUP_ACT)으로 고른다 — 메타는 그대로, 화면에서 칼럼 정의만 덮어씀.
 *   - 그리드 헤더 버튼(WBS 템플릿 적용 등)은 sys_headerctrl(cd_menu=팝업코드, cd_form='grid1') 메타.
 *   - 저장: 삭제 → 추가 → 수정 순(SN_SFA_PJT_REG_002_WBS_SAVE_01).
 */
// WBS현황(간트)과 같은 순서: 대표ACT(부모 없음) 를 조회 순서대로, 각 대표ACT 바로 아래에 그 세부ACT 를 조회 순서대로.
// (SN_SFA_PJT_REG_002.buildProjects + WbsGantt 행 전개와 동일 규칙)
const childTest = (rows: any[]) => {
  const levels = new Set(rows.map((r) => String(r.cd_level)));
  return (r: any) =>
    !!r.cd_plevel && levels.has(String(r.cd_plevel)) && String(r.cd_plevel) !== String(r.cd_level);
};
const orderLikeGantt = (rows: any[]): any[] => {
  const isChild = childTest(rows);
  const kids = new Map<string, any[]>();
  rows.filter(isChild).forEach((r) => {
    const k = String(r.cd_plevel);
    kids.set(k, [...(kids.get(k) || []), r]);
  });
  const out: any[] = [];
  rows
    .filter((r) => !isChild(r))
    .forEach((root) => {
      out.push(root);
      out.push(...(kids.get(String(root.cd_level)) || []));
    });
  return out;
};

const POPUP_CODE = "SN_SFA_PJT_REG_002_POP_002";

// 같은 저장 안에서 부모-자식을 잇기 위한 신규 cd_level (varchar(30), 프로젝트 안에서만 유일하면 됨)
const newLevelCode = () => `N${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

interface popupProps {
  modalRef?: any;
  selectedData?: any; // { cd_pjt } — 부모 조회조건 프로젝트 프리셀렉트
}

const SN_SFA_PJT_REG_002_POP_002 = forwardRef(({ modalRef, selectedData }: popupProps, ref: React.Ref<any>) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRef = useRef<any>(null);

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [popupParent, setPopupParent] = useState<any>(null);
  const [headerInfo, setHeaderInfo] = useState<{ controls: any[]; data: any }>({ controls: [], data: {} });

  useImperativeHandle(ref, () => ({
    checkValidity: async () => await onSave(),
    getOkayData: async () => ({ closeFlag: true, rtnData: [] }),
  }));

  useEffect(() => {
    if (modalRef?.current) setPopupParent(modalRef.current.getPopupParent());
  }, [modalRef?.current]);

  useEffect(() => {
    (async () => {
      try {
        const rtn: any = await getInitPopUpData(POPUP_CODE, {});
        const merged = { ...rtn.formData, ...selectedData };
        setFormControlData(rtn.formControlData);
        setFormData(merged);
        setColumns(rtn.grid?.grid1 || []);
        onSearch(merged);
        // 헤더 버튼 메타 — getInitPopUp 은 sys_headerctrl 을 내려주지 않아 직접 읽는다
        const hdr: any = await fnGetTableById({
          id: "popup_headerctrl",
          params: { cd_menu: POPUP_CODE, cd_form: "grid1" },
        });
        setHeaderInfo({ controls: Array.isArray(hdr) ? hdr : [], data: {} });
      } catch (e) {
        /* restApi 에서 처리 */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = async (data: any) => {
    try {
      const res: any = await fnGetDataSet({
        id: "sn_sfa_pjt_reg_002_01",
        params: {
          cd_firm: userInfo.cd_firm,
          cd_biz: "", // 사업장 구분 없이 회사 전체 (조회조건 프로젝트 콤보와 동일 범위)
          cd_pjt: data?.cd_pjt || "",
          yn_use: "Y",
        },
      });
      // 쿼리가 cd_pjt 필터를 안 해도 선택 프로젝트만 보이도록 가드
      const rows = (res?.[0] || []).filter(
        (r: any) => !data?.cd_pjt || r.cd_pjt === data.cd_pjt
      );
      // 대표ACT(level1) 표시용 nm_lead 주입 — 부모(cd_plevel)의 nm_activity
      const nameByLevel = new Map<string, string>(
        rows.map((r: any) => [String(r.cd_level), r.nm_activity])
      );
      rows.forEach((r: any) => {
        r.nm_lead = nameByLevel.get(String(r.cd_plevel)) ?? "";
      });
      setDataSource(orderLikeGantt(rows));
      gridRef.current?.resetUpdate();
    } catch (e) {
      setDataSource([]);
    }
  };

  // 조회조건(프로젝트) 변경 시 재조회
  const onValueChanged = (event: any) => {
    const next = event?.rowData ?? formData;
    setFormData(next);
    if (event?.colId === "cd_pjt") onSearch(next);
  };

  // 그리드의 현재 행(삭제된 행 제외)
  const currentRows = (): any[] => {
    const rows: any[] = [];
    gridRef.current?.forEachNode((n: any) => n?.data && rows.push(n.data));
    return rows;
  };

  // 행추가: 선택한 행이 있으면 그 대표ACT 아래 세부ACT 로 넣을지 묻고, 아니면 대표ACT 로 넣는다
  const onAddRow = async () => {
    const cdPjt = folderPjt;
    if (!cdPjt) {
      await showDialog("alter", "프로젝트를 선택해 주시기 바랍니다.");
      return;
    }
    gridRef.current?.stopEditing();
    const rows = currentRows();
    const focusedNode = gridRef.current?.focusedRowNode?.();
    const focused = focusedNode?.data;
    const parent = focused ? (focused.cd_plevel ? rows.find((r) => r.cd_level === focused.cd_plevel) : focused) : undefined;
    // 표시 위치: 세부ACT 는 선택 행 바로 아래, 대표ACT 는 선택 행이 속한 대표ACT 묶음 끝 아래(선택 없으면 맨 끝)
    const rowIndexOf = (data: any) => {
      let idx = -1;
      gridRef.current?.forEachNode((n: any) => {
        if (n.data === data && n.rowIndex != null) idx = n.rowIndex;
      });
      return idx;
    };
    const belowFocused = focusedNode?.rowIndex != null ? focusedNode.rowIndex + 1 : undefined;
    const belowGroup = parent
      ? Math.max(...[parent, ...rows.filter((r) => r.cd_plevel === parent.cd_level)].map(rowIndexOf)) + 1
      : undefined;

    let asChild = false;
    if (parent) {
      asChild = !!(await showDialog(
        "confirm",
        `대표ACT [${parent.nm_activity || parent.cd_activity}] 아래에 세부ACT 로 추가하시겠습니까?\n(아니오: 새 대표ACT 로 추가)`
      ));
    }
    const maxSeq = (list: any[]) => Math.max(0, ...list.map((r) => Number(r.no_seq) || 0));

    // ACTIVITY 는 도움창에서 먼저 고른다 — 체크(또는 담기)로 여러 개 선택 가능, 취소하면 행을 만들지 않음
    const picked: any = await showPopupModal({
      title: PopupSettings.POPUP_ACT.title,
      height: "75%",
      children: <MultiPopup code="POPUP_ACT" selectedData={{ str_search: "" }} />,
    });
    const acts: any[] = (Array.isArray(picked) ? picked : []).filter((a: any) => a?.cd_activity);
    if (acts.length === 0) return;

    // 고른 순서대로 이어서 넣는다 (i 번째는 기준 위치 + i)
    if (asChild && parent) {
      const kids = rows.filter((r) => r.cd_plevel === parent.cd_level);
      acts.forEach((act, i) => {
        gridRef.current?.addRow({
          cd_pjt: cdPjt,
          cd_activity: act.cd_activity,
          nm_activity: act.nm_activity || act.cd_activity,
          cd_level: newLevelCode(),
          cd_plevel: parent.cd_level,
          nm_lead: parent.nm_activity || parent.cd_activity || "",
          no_level: `${parent.no_level || ""}.${kids.length + 1 + i}`,
          no_seq: maxSeq([parent, ...kids]),
          rt_weight: 0,
        }, undefined, undefined, belowFocused != null ? belowFocused + i : undefined);
      });
      return;
    }
    const roots = rows.filter((r) => !r.cd_plevel);
    const maxRootLevel = Math.max(0, ...roots.map((r) => parseInt(r.no_level, 10) || 0));
    acts.forEach((act, i) => {
      gridRef.current?.addRow({
        cd_pjt: cdPjt,
        cd_activity: act.cd_activity,
        nm_activity: act.nm_activity || act.cd_activity,
        cd_level: newLevelCode(),
        cd_plevel: "",
        nm_lead: "",
        no_level: String(maxRootLevel + 1 + i),
        no_seq: maxSeq(rows) + 1 + i,
        rt_weight: 0,
      }, undefined, undefined, belowGroup != null ? belowGroup + i : undefined);
    });
  };

  // 행삭제: 대표ACT 는 세부ACT 까지 함께. 실적(실적시작일)이 있는 행이 하나라도 있으면 막는다
  const onDeleteRow = async () => {
    const target = gridRef.current?.focusedRowNode?.()?.data;
    if (!target) return;
    const rows = currentRows();
    const victims = [target, ...(target.cd_plevel ? [] : rows.filter((r) => r.cd_plevel === target.cd_level))];
    const hasWork = victims.find((r) => r.dt_work_start);
    if (hasWork) {
      await showDialog(
        "alter",
        `[${hasWork.nm_activity || hasWork.cd_activity || ""}] 실적이 입력된 행이 있어 삭제할 수 없습니다.`
      );
      return;
    }
    const name = target.nm_activity || target.cd_activity || "신규 행";
    const ok = await showDialog(
      "confirm",
      victims.length > 1
        ? `대표ACT [${name}] 와 세부ACT ${victims.length - 1}건을 함께 삭제하시겠습니까?`
        : `[${name}] 행을 삭제하시겠습니까?`
    );
    if (!ok) return;
    // 아래 행부터 지워야 앞 행의 표시 인덱스가 밀리지 않는다
    const idx: number[] = [];
    gridRef.current?.forEachNode((n: any) => {
      if (victims.includes(n.data) && n.rowIndex != null) idx.push(n.rowIndex);
    });
    idx.sort((a, b) => b - a).forEach((i) => gridRef.current?.deleteRowByIndex(i));
  };

  // 그리드 헤더 버튼(sys_headerctrl)
  const onHeaderBtnClick = async (id: string) => {
    if (id === "btnApplyTemplate") {
      await showDialog("alter", "WBS 템플릿 적용은 준비 중입니다.");
    }
  };

  // 대표ACT 가중치 = 세부ACT 가중치 합계 (세부ACT 가 있는 대표ACT 만). 변경 시 저장 대상에 포함된다.
  const syncParentWeights = () => {
    const nodes: any[] = [];
    gridRef.current?.forEachNode((node: any) => node?.data && nodes.push(node));
    const isChild = childTest(nodes.map((n) => n.data));
    const sums = new Map<string, number>();
    nodes.forEach((n) => {
      if (!isChild(n.data)) return;
      const k = String(n.data.cd_plevel);
      sums.set(k, (sums.get(k) || 0) + (Number(n.data.rt_weight) || 0));
    });
    nodes.forEach((n) => {
      const sum = sums.get(String(n.data.cd_level));
      if (sum == null || isChild(n.data)) return;
      const v = Math.round(sum * 100) / 100;
      if ((Number(n.data.rt_weight) || 0) !== v) {
        if (n.data.state !== "insert") n.data.state = "update";
        n.setDataValue("rt_weight", v, "noEvent");
      }
    });
  };

  const onSave = async () => {
    try {
      gridRef.current?.stopEditing();

      // 가중치 검증 — 대표ACT 가중치(세부ACT 가 있으면 그 합계로 자동 계산) 합계 100
      syncParentWeights();
      let rootSum = 0;
      const all: any[] = [];
      gridRef.current?.forEachNode((node: any) => node?.data && all.push(node.data));
      const isChild = childTest(all);
      all.filter((r) => !isChild(r)).forEach((r) => (rootSum += Number(r.rt_weight) || 0));
      rootSum = Math.round(rootSum * 100) / 100;
      if (Math.abs(rootSum - 100) > 0.01) {
        await showDialog("alter", `대표ACT 가중치 합계가 100 이어야 합니다. (현재 ${rootSum})\n세부ACT 가 있는 대표ACT 는 세부ACT 가중치 합계로 계산됩니다.`);
        return false;
      }

      const chg = gridRef.current?.getChange();
      if (!chg || chg.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const updates: any[] = chg.update || [];
      const inserts: any[] = chg.insert || [];
      const deletes: any[] = (chg.delete || []).filter((r: any) => r.no_line != null && r.no_line !== "");

      // 행추가가 있으면 화면 표시 순서대로 순번(no_seq)을 다시 매긴다 — 재조회(no_seq 순)해도 넣은 위치가 유지되게
      const seqOf = new Map<any, number>();
      const orders: any[] = [];
      if (inserts.length > 0) {
        const shown: any[] = [];
        gridRef.current?.forEachNode((n: any) => n?.data && n.rowIndex != null && shown.push(n));
        shown.sort((a, b) => a.rowIndex - b.rowIndex).forEach((n, i) => seqOf.set(n.data, i + 1));
        shown.forEach((n) => {
          const d = n.data;
          if (d.state !== "insert" && d.no_line != null && Number(d.no_seq) !== seqOf.get(d)) {
            orders.push({ cd_pjt: d.cd_pjt, no_line: d.no_line, no_seq: seqOf.get(d) });
          }
        });
      }
      const noAct = inserts.find((r) => !String(r.cd_activity || "").trim());
      if (noAct) {
        await showDialog("alter", "추가한 행의 ACTIVITY 를 선택해 주시기 바랍니다.");
        return false;
      }
      for (const r of [...updates, ...inserts]) {
        const ds = String(r.dt_start || ""), de = String(r.dt_end || "");
        const ws = String(r.dt_work_start || ""), we = String(r.dt_work_end || "");
        if (ds && de && de < ds) {
          await showDialog("alter", `[${r.nm_activity || r.cd_activity}] 계획종료일이 계획시작일보다 빠릅니다.`);
          return false;
        }
        if (ws && we && we < ws) {
          await showDialog("alter", `[${r.nm_activity || r.cd_activity}] 실적종료일이 실적시작일보다 빠릅니다.`);
          return false;
        }
      }

      const ok = await callApi("SN_SFA_PJT_REG_002_WBS_SAVE_01", {
        cd_firm: userInfo.cd_firm,
        rows: updates.map((r) => ({
          cd_pjt: r.cd_pjt,
          no_line: r.no_line,
          dt_start: r.dt_start || "",
          dt_end: r.dt_end || "",
          dt_work_start: r.dt_work_start || "",
          dt_work_end: r.dt_work_end || "",
          rt_weight: r.rt_weight,
        })),
        // 추가 행은 화면 순서대로(대표ACT 가 자식보다 먼저) — no_line 은 서버가 매긴다
        inserts: inserts.map((r) => ({
          cd_pjt: r.cd_pjt,
          cd_activity: r.cd_activity,
          cd_level: r.cd_level,
          cd_plevel: r.cd_plevel || "",
          no_level: r.no_level || "",
          no_seq: seqOf.get(r) ?? r.no_seq ?? 0,
          dt_start: r.dt_start || "",
          dt_end: r.dt_end || "",
          dt_work_start: r.dt_work_start || "",
          dt_work_end: r.dt_work_end || "",
          rt_weight: r.rt_weight ?? 0,
        })),
        deletes: deletes.map((r) => ({ cd_pjt: r.cd_pjt, no_line: r.no_line })),
        orders, // 기존 행 순번 변경분

      });
      if (!ok?.status) return false; // callApi 는 항상 객체를 돌려주므로 status 로 판단

      gridRef.current?.resetUpdate();
      return true;
    } catch (e) {
      gridRef.current?.resetUpdate(true);
      return false;
    }
  };

  const grid = useMemo(
    () => ({ columns, dataSource }),
    [columns, dataSource]
  );

  // 공유폴더 대상 프로젝트: 조회조건 값, 없으면 조회된 WBS 가 한 프로젝트뿐일 때 그 프로젝트
  const folderPjt = useMemo(() => {
    if (formData?.cd_pjt) return String(formData.cd_pjt);
    const pjts = new Set(dataSource.map((r: any) => r.cd_pjt));
    return pjts.size === 1 ? String([...pjts][0]) : "";
  }, [formData?.cd_pjt, dataSource]);

  // 공유폴더 자동 생성용 WBS 대표ACT 이름 (예: "1.분석")
  const rootNames = useMemo(
    () =>
      dataSource
        .filter((r: any) => !r.nm_lead)
        .map((r: any) => `${r.no_level ? `${r.no_level}.` : ""}${r.nm_activity || r.cd_activity || ""}`),
    [dataSource]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-2">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          popupRef={ref}
          onValueChanged={onValueChanged}
        />
      </div>
      <div className="flex-1 flex flex-col min-h-0">
        <SnGrid
          ref={gridRef}
          columnConfig={grid.columns}
          rowData={grid.dataSource}
          headerInfo={headerInfo}
          events={{
            onBtnAddClick: onAddRow,
            onBtnDeleteClick: onDeleteRow,
            onBtnEtcClick: onHeaderBtnClick,
            onCellValueChanged: (e: any) => e.colId === "rt_weight" && syncParentWeights(),
            // 합계행은 대표ACT 만 합산 — 대표ACT 가중치가 이미 세부ACT 합계라 같이 더하면 이중 합산된다
            sumRowFilter: (row: any) => {
              const all: any[] = [];
              gridRef.current?.forEachNode((n: any) => n?.data && all.push(n.data));
              return !childTest(all)(row);
            },
          }}
          etcProps={{
            title: "WBS 진행",
            rowSelection: "single",
            popupParent: popupParent,
            isEditable: true,
            enableInitialSort: false, // 첫 컬럼 자동정렬 대신 WBS현황 순서 유지
          }}
        />
      </div>
      {/* 프로젝트 공유폴더 (폴더 트리 첨부) */}
      <div className="mt-2 shrink-0" style={{ height: 230 }}>
        <ProjectFolder cdPjt={folderPjt} rootNames={rootNames} />
      </div>
    </div>
  );
});

export default SN_SFA_PJT_REG_002_POP_002;
